import React, { useRef, useState } from 'react';
import { Button, message, Result, Space, Spin, Tag, Tooltip } from 'antd';
import {
  ReloadOutlined,
  ScissorOutlined,
  LoadingOutlined,
  FileOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import AlignmentPDFPreview, {
  AlignmentPDFPreviewRef,
} from '../alignments/AlignmentPDFPreview';

dayjs.extend(localizedFormat);

interface UnknownFormFile {
  onedrive_file_id: string;
  name: string;
  submission_source: string;
  size: number;
  created_at: string;
  mime_type: string;
  thumbnail_url: string | null;
}

const formatSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const FileCard: React.FC<{
  file: UnknownFormFile;
  onSplitComplete: () => void;
}> = ({ file, onSplitComplete }) => {
  const previewRef = useRef<AlignmentPDFPreviewRef>(null);
  const [isReady, setIsReady] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      await axios.post(
        `${process.env.REACT_APP_CHALICE_URL}/discard_unknown_form`,
        { onedrive_file_id: file.onedrive_file_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('File discarded');
      onSplitComplete();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error?.message || 'Failed to discard'
      );
    } finally {
      setIsDiscarding(false);
    }
  };

  const pageCountRequest = useRequest(
    async () => {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      const response = await axios.get(
        `${process.env.REACT_APP_CHALICE_URL}/get_onedrive_page_count`,
        {
          params: { onedrive_file_id: file.onedrive_file_id },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.page_count as number;
    },
    { cacheKey: `page_count_${file.onedrive_file_id}` }
  );

  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <Tooltip title={file.name}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.name}
        </div>
      </Tooltip>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Tag>{file.submission_source}</Tag>
        <span style={{ fontSize: 11, color: '#8c8c8c' }}>
          {dayjs(file.created_at).format('lll')}
        </span>
      </div>
      <div
        style={{
          height: 180,
          backgroundColor: '#fafafa',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {file.thumbnail_url ? (
          <img
            src={file.thumbnail_url}
            alt={file.name}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        ) : (
          <FileOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
        )}
      </div>
      {/* Hidden component to handle split modal */}
      <div style={{ display: 'none' }}>
        <AlignmentPDFPreview
          ref={previewRef}
          submission_id={file.name}
          submission_source={file.submission_source}
          onedrive_file_id={file.onedrive_file_id}
          onReady={() => setIsReady(true)}
          onSplitComplete={onSplitComplete}
          hideSplitButton
          iframeHeight={0}
          moveOriginalAfterSplit
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12, color: '#595959' }}>
          {pageCountRequest.loading ? (
            <LoadingOutlined />
          ) : pageCountRequest.data != null ? (
            `${pageCountRequest.data} page${
              pageCountRequest.data !== 1 ? 's' : ''
            }`
          ) : (
            ''
          )}
        </span>
        <span style={{ fontSize: 11, color: '#8c8c8c' }}>
          {formatSize(file.size)}
        </span>
      </div>
      <Space>
        <Button
          size="small"
          icon={<DeleteOutlined />}
          loading={isDiscarding || !isReady}
          onClick={handleDiscard}
        >
          Discard
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<ScissorOutlined />}
          loading={!isReady}
          onClick={() => previewRef.current?.openSplitModal()}
        >
          Split and Move Pages
        </Button>
      </Space>
    </div>
  );
};

const UnknownFormsGrid: React.FC = () => {
  const fetchFiles = async (): Promise<{
    files: UnknownFormFile[];
    count: number;
    total_count: number;
  }> => {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken();
    const response = await axios.get(
      `${process.env.REACT_APP_CHALICE_URL}/list_unknown_forms`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const filesRequest = useRequest(fetchFiles);

  const files = filesRequest.data?.files || [];
  const totalCount = filesRequest.data?.total_count ?? 0;

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <span style={{ color: '#595959' }}>
          {filesRequest.loading
            ? 'Loading...'
            : `Showing ${files.length} of ${totalCount} file${
                totalCount !== 1 ? 's' : ''
              } in Unknown Forms`}
        </span>
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={filesRequest.refresh}
          loading={filesRequest.loading}
        >
          Refresh
        </Button>
      </div>

      {filesRequest.error ? (
        <Result
          status="error"
          title="Error loading unknown forms"
          subTitle={filesRequest.error.message}
        />
      ) : (
        <Spin spinning={filesRequest.loading && files.length === 0}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 16,
            }}
          >
            {files.map((file) => (
              <FileCard
                key={file.onedrive_file_id}
                file={file}
                onSplitComplete={filesRequest.refresh}
              />
            ))}
          </div>
        </Spin>
      )}
    </>
  );
};

export default UnknownFormsGrid;
